import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal, LineReveal } from "./Reveal";

// sections: [{ id, number, title, content: <JSX> }]
export const LegalLayout = ({ eyebrow, titleLines, meta, intro, sections }) => {
  const [active, setActive] = useState(sections[0]?.id);
  const observer = useRef(null);

  useEffect(() => {
    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    const els = sections.map((s) => document.getElementById(s.id)).filter(Boolean);
    els.forEach((el) => observer.current.observe(el));
    return () => observer.current?.disconnect();
  }, [sections]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -110 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative z-10 pt-28 md:pt-36 pb-24">
      {/* header */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Link
          to="/"
          data-testid="back-to-legal-home"
          className="group inline-flex items-center gap-2 font-mono-meta text-[11px] uppercase tracking-[0.25em] text-[#8A8A8A] hover:text-[#C6826D] transition-colors"
        >
          <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
          Back to legal home
        </Link>

        <div className="mt-10 md:mt-14 border-b border-[#E0E0D9] pb-12 md:pb-16">
          <div className="font-mono-meta text-[11px] uppercase tracking-[0.3em] text-[#C6826D]">
            {eyebrow}
          </div>
          <h1 className="mt-6 font-serif-display font-light tracking-tight leading-[0.95] text-5xl sm:text-6xl md:text-7xl">
            <LineReveal lines={titleLines} />
          </h1>
          {meta && (
            <div className="mt-8 font-mono-meta text-[11px] uppercase tracking-[0.2em] text-[#8A8A8A]">
              {meta}
            </div>
          )}
          {intro && (
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-2xl text-lg text-[#525252] leading-relaxed">{intro}</p>
            </Reveal>
          )}
        </div>
      </div>

      {/* body: sticky TOC + content */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-14 md:mt-20 flex flex-col md:flex-row gap-14 md:gap-24">
        <aside className="w-full md:w-1/4 md:sticky md:top-28 self-start">
          <div className="font-mono-meta text-[11px] uppercase tracking-[0.25em] text-[#8A8A8A] mb-6">
            Contents
          </div>
          <nav className="flex flex-col gap-1">
            {sections.map((s) => (
              <button
                key={s.id}
                data-testid={`toc-${s.id}`}
                onClick={() => scrollTo(s.id)}
                className={`group text-left py-2 flex items-baseline gap-3 transition-colors duration-200 ${
                  active === s.id ? "text-[#0A0A0A]" : "text-[#8A8A8A] hover:text-[#525252]"
                }`}
              >
                <span className="font-mono-meta text-[11px] tabular-nums">{s.number}</span>
                <span className="text-sm leading-snug border-b border-transparent group-hover:border-[#C6826D]">
                  {s.title}
                </span>
              </button>
            ))}
          </nav>
        </aside>

        <div className="w-full md:w-3/4 max-w-3xl">
          {sections.map((s) => (
            <section
              key={s.id}
              id={s.id}
              data-testid={`section-${s.id}`}
              className="scroll-mt-28 mb-16 md:mb-24"
            >
              <Reveal>
                <div className="flex items-baseline gap-5 mb-7">
                  <span className="font-mono-meta text-sm text-[#C6826D]">{s.number}</span>
                  <h2 className="font-serif-display text-3xl md:text-4xl font-normal tracking-tight leading-tight">
                    {s.title}
                  </h2>
                </div>
                <div className="legal-prose">{s.content}</div>
              </Reveal>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

// Reusable data table for legal docs
export const LegalTable = ({ head, rows }) => (
  <div className="my-8 overflow-x-auto border border-[#E0E0D9]">
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="bg-[#F3F3EF]">
          {head.map((h, i) => (
            <th
              key={i}
              className="text-left font-mono-meta text-[10px] uppercase tracking-[0.15em] text-[#8A8A8A] px-5 py-4 border-b border-[#E0E0D9] align-top"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri} className="align-top">
            {r.map((c, ci) => (
              <td
                key={ci}
                className={`px-5 py-4 border-b border-[#E0E0D9] leading-relaxed ${
                  ci === 0 ? "font-mono-meta text-[#0A0A0A] whitespace-nowrap" : "text-[#525252]"
                }`}
              >
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
